/**
 * YouTubeLinkForm - Form for adding YouTube link recordings
 * Extracted from RecordingsTab.jsx
 */
import { motion } from "framer-motion";
import { Youtube, X } from "lucide-react";
import { Button } from "../../ui/button";

const SPORTS_LIST = [
  "Football", "Basketball", "Boys Basketball", "Girls Basketball", 
  "Mens Basketball", "Womens Basketball", "Soccer", "Baseball", 
  "Softball", "Volleyball", "Wrestling", "Tennis", "Golf", 
  "Track & Field", "Swimming", "Lacrosse", "Hockey", "Highlights", "Other"
];

export const YouTubeLinkForm = ({ 
  youtubeForm, 
  setYoutubeForm, 
  onSave, 
  onClose, 
  saving 
}) => (
  <motion.div 
    initial={{ opacity: 0, y: -10 }} 
    animate={{ opacity: 1, y: 0 }} 
    className="p-6 bg-zinc-900/50 border border-red-500/30 space-y-6"
  >
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3">
        <Youtube className="w-5 h-5 text-red-500" />
        <div>
          <h3 className="font-secondary text-lg font-bold uppercase text-white">Add YouTube Link</h3>
          <p className="text-xs text-zinc-500">Add a YouTube video to On Demand. Duration will be auto-fetched.</p>
        </div>
      </div>
      <button type="button" onClick={onClose} className="text-zinc-500 hover:text-white">
        <X className="w-5 h-5" />
      </button>
    </div>
    
    {/* YouTube URL - Most Important */}
    <div className="bg-red-500/10 border border-red-500/30 p-4">
      <label className="block text-xs font-bold uppercase tracking-widest text-red-400 mb-2">YouTube URL *</label>
      <input 
        type="url" 
        value={youtubeForm.youtube_url} 
        onChange={(e) => setYoutubeForm({ ...youtubeForm, youtube_url: e.target.value })} 
        placeholder="https://www.youtube.com/watch?v=..." 
        className="w-full bg-zinc-800 border border-red-500/50 text-white px-3 py-3 text-sm font-mono"
        data-testid="youtube-url-input"
      />
    </div>
    
    {/* Title */}
    <div>
      <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">Title *</label>
      <input 
        type="text" 
        value={youtubeForm.title} 
        onChange={(e) => setYoutubeForm({ ...youtubeForm, title: e.target.value })} 
        placeholder="Video Title" 
        className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm"
        data-testid="youtube-title-input"
      />
    </div>
    
    {/* Basic Info Row */}
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <div>
        <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">Sport</label>
        <select 
          value={youtubeForm.sport} 
          onChange={(e) => setYoutubeForm({ ...youtubeForm, sport: e.target.value })} 
          className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm"
        >
          {SPORTS_LIST.map(s => <option key={s}>{s}</option>)}
        </select>
      </div>
      <div>
        <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">Date</label>
        <input 
          type="date" 
          value={youtubeForm.recorded_at} 
          onChange={(e) => setYoutubeForm({ ...youtubeForm, recorded_at: e.target.value })} 
          className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm" 
        />
      </div>
      <div>
        <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">Description (Optional)</label>
        <input 
          type="text" 
          value={youtubeForm.description} 
          onChange={(e) => setYoutubeForm({ ...youtubeForm, description: e.target.value })} 
          placeholder="Brief description..." 
          className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm" 
        />
      </div>
    </div>
    
    {/* Thumbnail URL (Optional) */}
    <div>
      <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">Custom Thumbnail URL (Optional)</label>
      <input 
        type="url" 
        value={youtubeForm.thumbnail_url} 
        onChange={(e) => setYoutubeForm({ ...youtubeForm, thumbnail_url: e.target.value })} 
        placeholder="Leave blank to use YouTube thumbnail" 
        className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm" 
      />
      <p className="text-[10px] text-zinc-600 mt-1">If left blank, the YouTube thumbnail will be used automatically.</p>
    </div>
    
    {/* Publish Toggle */}
    <div className="flex items-center gap-3">
      <input 
        type="checkbox" 
        id="youtube_is_published" 
        checked={youtubeForm.is_published} 
        onChange={(e) => setYoutubeForm({ ...youtubeForm, is_published: e.target.checked })}
        className="w-4 h-4"
      />
      <label htmlFor="youtube_is_published" className="text-sm text-zinc-400">
        Publish immediately to On Demand page
      </label>
    </div>
    
    {/* Actions */}
    <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
      <Button
        type="button"
        onClick={onClose}
        className="bg-zinc-800 text-white hover:bg-zinc-700 font-secondary text-xs px-6 py-2 h-auto rounded-none"
      >
        Cancel
      </Button>
      <Button
        type="button"
        onClick={onSave}
        disabled={saving}
        className="bg-red-600 text-white hover:bg-red-700 font-secondary font-bold text-xs px-6 py-2 h-auto rounded-none"
        data-testid="save-youtube-recording-btn"
      >
        {saving ? "Saving..." : "Add YouTube Video"}
      </Button>
    </div>
  </motion.div>
);

export default YouTubeLinkForm;
